var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as Rx from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
/**
 * Batches requests that occur within `windowTime` milliseconds of each other,
 * such that at the end of the interval, requests to the same slice of the
 * datastore are grouped together into one request. Requests will not be batched
 * if they differ in either collection or authUid.
 *
 * Example: With a window size of 3 seconds, a request for user with id 1 (U1),
 * then for ids 2 and 3 (U2:3) one second later, then for project 5 (P5)
 *
 * 0     1     2     3     4     5s
 * U1----U2:3----P5--------------|
 *        batchRequests(3000)
 * ------------------B-----------|
 *
 * Where B is the list [ U1:2:3, P5 ], with the first request for users 1, 2, 3
 * and the second for project 5
 */
export function batchRequests(windowTime, scheduler) {
    if (scheduler === void 0) { scheduler = Rx.asyncScheduler; }
    if (windowTime === 0) {
        return function (source$) {
            return source$.pipe(map(function (request) { return [request]; }));
        };
    }
    var requestsBuffer = [];
    return function (requestStream$) {
        return requestStream$.pipe(mergeMap(function (request) {
            if (requestsBuffer.length === 0) {
                requestsBuffer.push(request);
                // We do want to run the timer inside the Angular Zone here as
                // otherwise the zone will stabilized before that the batched
                // requests have been run, i.e. data will be missing on the page in
                // e2e tests, web worker, or server-side rendering scenarios.
                // tslint:disable-next-line:validate-timers
                return Rx.timer(windowTime, scheduler).pipe(map(function () { return requestsBuffer.splice(0, requestsBuffer.length); }));
            }
            requestsBuffer.push(request);
            return Rx.EMPTY;
        }), map(function (requests) {
            var requestsToBatch = requests.reduce(function (toBatch, req) {
                var _a;
                var key = req.type + "-" + req.ref.path.authUid;
                var ids = req.ref.path.ids || [];
                var existingIds = [];
                // TODO: Add batch limits to prevent overloading backend
                if (toBatch[key]) {
                    var existingClientRequestIds_1 = toBatch[key].clientRequestIds;
                    existingIds = toBatch[key].ref.path.ids || [];
                    toBatch[key] = __assign({}, toBatch[key], { ref: __assign({}, toBatch[key].ref, { path: __assign({}, toBatch[key].ref.path, { ids: existingIds.concat(ids) }) }), 
                        // A batched request id is the concatenation of the orginal request ids
                        clientRequestIds: existingClientRequestIds_1.concat(req.clientRequestIds.filter(function (id) { return !existingClientRequestIds_1.includes(id); })) });
                    return toBatch;
                }
                return __assign({}, toBatch, (_a = {}, _a[key] = {
                    type: req.ref.path.collection,
                    ref: {
                        path: {
                            collection: req.ref.path.collection,
                            authUid: req.ref.path.authUid,
                            ids: ids,
                        },
                    },
                    clientRequestIds: req.clientRequestIds,
                }, _a));
            }, {});
            return Object.values(requestsToBatch);
        }));
    };
}
